// Kas & Bank — Dialog Bulk Classify (Phase 2, 2026-02).
// Terapkan 1 klasifikasi ke N mutasi terpilih; validasi arah dilakukan backend
// (baris tidak cocok arah → di-skipped, ditampilkan di panel hasil).
import { useEffect, useMemo, useState } from "react";
import { X, Layers, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "@/lib/constants";
import {
  KIND_META, KIND_ORDER, bulkClassify, fetchCOA, isInbound,
  suggestCOA, buildCoaGroupsWithSuggestions,
} from "@/lib/kasBankClassification";
import SearchableSelect from "@/components/SearchableSelect";

export default function BulkClassifyDialog({ rows, defaultKind, onClose, onSaved }) {
  const validRows = useMemo(() => (rows || []).filter((r) => r?.movement_id), [rows]);
  const noIdCount = (rows?.length || 0) - validRows.length;

  // Hitung arah gabungan untuk memberi hint kind yg diizinkan.
  const dirCounts = useMemo(() => {
    let inbound = 0, outbound = 0;
    for (const r of validRows) (isInbound(r.movement_id) ? inbound++ : outbound++);
    return { inbound, outbound };
  }, [validRows]);
  const allowedKinds = useMemo(() => {
    // Selalu boleh Transfer. Penerimaan hanya sah jika ada baris inbound; Pembayaran hanya jika ada outbound.
    const set = new Set(["transfer"]);
    if (dirCounts.inbound > 0) set.add("penerimaan");
    if (dirCounts.outbound > 0) set.add("pembayaran");
    return set;
  }, [dirCounts]);

  const [kind, setKind] = useState(defaultKind && allowedKinds.has(defaultKind) ? defaultKind : Array.from(allowedKinds)[0]);
  const [coaId, setCoaId] = useState("");
  const [docNo, setDocNo] = useState("");
  const [note, setNote] = useState("");
  const [coa, setCoa] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // 2026-02 (user request): saran COA agregat berdasarkan kata kunci per COA.
  const [suggested, setSuggested] = useState([]);
  const [autoSelectedRatio, setAutoSelectedRatio] = useState(0);

  useEffect(() => { fetchCOA().then(setCoa).catch(() => setCoa([])); }, []);
  useEffect(() => {
    let cancelled = false;
    const descs = Array.from(new Set(validRows.map((r) => (r?.description || "").trim()).filter(Boolean)));
    if (descs.length === 0) return;
    suggestCOA(descs).then((sMap) => {
      if (cancelled) return;
      const votes = new Map();
      for (const r of validRows) {
        const top = (sMap[r.description] || [])[0];
        if (!top) continue;
        const cur = votes.get(top.coa_id) || { count: 0, meta: top };
        cur.count += 1;
        votes.set(top.coa_id, cur);
      }
      const ranked = Array.from(votes.values()).sort((a, b) => b.count - a.count);
      const agg = ranked.map((v) => ({ ...v.meta, count: v.count }));
      setSuggested(agg);
      if (agg.length > 0) {
        const ratio = agg[0].count / validRows.length;
        setAutoSelectedRatio(ratio);
        if (ratio >= 0.5 && !coaId) setCoaId(agg[0].coa_id);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validRows]);

  const totalAmount = useMemo(() => validRows.reduce((a, r) => a + (Number(r.amount) || 0), 0), [validRows]);

  const submit = async (e) => {
    e.preventDefault();
    if (validRows.length === 0) return;
    try {
      setSaving(true);
      const res = await bulkClassify({
        movement_ids: validRows.map((r) => r.movement_id),
        kind, coa_id: coaId || null, doc_no: docNo || null, note: note || null,
      });
      setResult(res);
      const savedN = (res?.saved || []).length;
      const skippedN = (res?.skipped || []).length;
      if (savedN > 0) toast.success(`${savedN} mutasi diklasifikasi. ${skippedN > 0 ? `${skippedN} dilewati (arah tidak cocok).` : ""}`);
      else toast.warning("Tidak ada mutasi yang diklasifikasi (arah tidak cocok).");
      onSaved?.(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menerapkan klasifikasi massal.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="kbc-bulk-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-2xl border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#FF5A1F]" />
            <div>
              <div className="overline text-[10px] text-slate-500 tracking-widest">KLASIFIKASI MASSAL</div>
              <div className="font-display text-base font-semibold text-[#0B1D3A]">Terapkan 1 Klasifikasi ke {validRows.length} Mutasi</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="kbc-bulk-close"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/70 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div><div className="text-slate-500">Total Baris</div><div className="font-bold tabular-nums text-[#0B1D3A]">{validRows.length}{noIdCount > 0 ? ` (+${noIdCount} legacy dilewati)` : ""}</div></div>
          <div><div className="text-slate-500">Mutasi Masuk</div><div className="font-bold tabular-nums text-emerald-700">{dirCounts.inbound}</div></div>
          <div><div className="text-slate-500">Mutasi Keluar</div><div className="font-bold tabular-nums text-rose-700">{dirCounts.outbound}</div></div>
          <div><div className="text-slate-500">Total Nominal</div><div className="font-bold tabular-nums text-[#0B1D3A]">{formatIDR(totalAmount)}</div></div>
        </div>

        {validRows.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-amber-800 bg-amber-50 border-b border-amber-200">
            Tidak ada mutasi valid pada seleksi Anda (baris legacy tanpa movement_id tidak dapat diklasifikasi).
          </div>
        ) : (
          <div className="p-4 space-y-3 text-[11px]">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Jenis</div>
              <div className="flex items-center gap-1 flex-wrap" data-testid="kbc-bulk-kind">
                {KIND_ORDER.map((k) => {
                  const disabledK = !allowedKinds.has(k);
                  const active = kind === k;
                  return (
                    <button key={k} type="button" disabled={disabledK} onClick={() => setKind(k)} data-testid={`kbc-bulk-kind-${k}`}
                      className={`px-3 py-1 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : disabledK ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}>
                      {KIND_META[k].label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Baris yang arahnya tidak cocok kind (mis. mutasi masuk dgn "Pembayaran") akan otomatis <b>dilewati</b> — bukan error.
              </div>
            </div>

            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Kategori Akun (COA)</div>
              {/* 2026-09-24 (user request): dropdown searchable. */}
              {/* 2026-02 (user request): grup "Disarankan" (agregat kata kunci COA). */}
              <SearchableSelect value={coaId} onChange={setCoaId}
                groups={buildCoaGroupsWithSuggestions(suggested, coa)}
                placeholder="— Tanpa Kategori —" testid="kbc-bulk-coa" />
              {suggested.length > 0 && (
                <div className="text-[10px] text-emerald-700 mt-1" data-testid="kbc-bulk-suggest-hint">
                  🔮 Disarankan: <b>{suggested[0].code} · {suggested[0].name}</b> · cocok pada {suggested[0].count} dari {validRows.length} baris
                  {autoSelectedRatio >= 0.5 ? " (auto-pilih)" : ""}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Nomor Dokumen (opsional)</div>
                <input value={docNo} onChange={(e) => setDocNo(e.target.value)} data-testid="kbc-bulk-docno" placeholder="Manual, mis. BM-2026/01" maxLength={64}
                  className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
              </div>
              <div>
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan (opsional)</div>
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="kbc-bulk-note" placeholder="Catatan bebas" maxLength={200}
                  className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
              </div>
            </div>

            {result && (
              <div className="border border-slate-200 bg-slate-50 p-3 text-[11px]" data-testid="kbc-bulk-result">
                <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><ArrowRight size={12} /> Hasil</div>
                <div>Berhasil: <b className="text-emerald-700 tabular-nums">{(result.saved || []).length}</b> · Dilewati: <b className="text-amber-700 tabular-nums">{(result.skipped || []).length}</b> · Total: <b className="tabular-nums">{result.total}</b></div>
                {(result.skipped || []).length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1">Baris dilewati karena arah tidak cocok dengan kind yang dipilih.</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <button type="button" onClick={onClose} data-testid="kbc-bulk-cancel"
            className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Tutup</button>
          <button type="submit" disabled={saving || validRows.length === 0} data-testid="kbc-bulk-save"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            <Layers size={12} /> {saving ? "Menerapkan…" : `Terapkan ke ${validRows.length} Mutasi`}
          </button>
        </div>
      </form>
    </div>
  );
}
